import type { EditableIndividualWithIdentity } from "@/lib/cat/individual-profile.types";
import { draftToEditableIndividualProfile } from "@/lib/cat/mdm-recap-from-draft";
import type { Draft } from "@/prisma/generated/client";

export type ClientStepPath = {
  rentSale: "no" | "yes" | null;
  directorSale: "no" | "yes" | null;
  exclusiveKind: "individual" | "company" | null;
};

export function isIndividualPanelActive(path: ClientStepPath): boolean {
  return (
    path.rentSale === "no" &&
    (path.directorSale === "yes" ||
      (path.directorSale === "no" && path.exclusiveKind === "individual"))
  );
}

export function clientStepPathFromDraft(draft: Draft | null): ClientStepPath {
  if (!draft) {
    return { rentSale: null, directorSale: null, exclusiveKind: null };
  }
  return {
    rentSale:
      draft.rentSale === true ? "yes" : draft.rentSale === false ? "no" : null,
    directorSale:
      draft.directorSale === true
        ? "yes"
        : draft.directorSale === false
          ? "no"
          : null,
    exclusiveKind:
      (draft.exclusiveKind as ClientStepPath["exclusiveKind"]) ?? null,
  };
}

export function hasDraftClientData(draft: Draft | null): boolean {
  if (!draft) return false;
  const lastName = draft.clientLastName?.trim();
  const email = draft.clientEmail?.trim();
  const phone =
    draft.clientMobilePhone?.trim() || draft.clientFixedPhone?.trim() || "";
  return Boolean(lastName || email || phone);
}

export function shouldHydrateIndividualFromDraft(draft: Draft | null): boolean {
  if (!draft || draft.rentSale) return false;
  if (!hasDraftClientData(draft)) return false;
  return Boolean(draft.directorSale || draft.exclusiveKind === "individual");
}

/** Restore individual data only when the UI path matches what was saved in draft. */
export function shouldHydrateIndividualForPath(
  draft: Draft | null,
  path: ClientStepPath,
): boolean {
  if (!shouldHydrateIndividualFromDraft(draft)) return false;
  if (!isIndividualPanelActive(path) || !draft) return false;

  if (draft.directorSale) {
    return path.directorSale === "yes";
  }
  if (draft.exclusiveKind === "individual") {
    return path.directorSale === "no" && path.exclusiveKind === "individual";
  }
  return false;
}

export function buildParticulierSearchCriteriaFromDraft(draft: Draft) {
  return {
    email: draft.clientEmail?.trim() ?? "",
    firstName: draft.clientFirstName?.trim() || undefined,
    lastName: draft.clientLastName?.trim() ?? "",
    fixedPhone: draft.clientFixedPhone?.trim() ?? "",
    mobilePhone: draft.clientMobilePhone?.trim() ?? "",
  };
}

export type ParticulierSyncSearchCriteria =
  | {
      valid: true;
      criteria: {
        email: string;
        firstName: string;
        lastName: string;
        fixedPhone: string;
        mobilePhone: string;
      };
    }
  | { valid: false };

/** Server re-fetch: firstName required (unlike UI search). */
export function buildParticulierSyncSearchCriteriaFromDraft(
  draft: Draft,
): ParticulierSyncSearchCriteria {
  const lastName = draft.clientLastName?.trim() ?? "";
  const firstName = draft.clientFirstName?.trim() ?? "";
  const email = draft.clientEmail?.trim() ?? "";
  const fixedPhone = draft.clientFixedPhone?.trim() ?? "";
  const mobilePhone = draft.clientMobilePhone?.trim() ?? "";

  if (!lastName || !firstName || (!email && !fixedPhone && !mobilePhone)) {
    return { valid: false };
  }

  return {
    valid: true,
    criteria: { email, firstName, lastName, fixedPhone, mobilePhone },
  };
}

export function draftToEditableIndividualWithIdentity(
  draft: Draft | null,
): EditableIndividualWithIdentity | null {
  const profile = draftToEditableIndividualProfile(draft);
  if (!profile || !draft) return null;
  return {
    ...profile,
    firstName: draft.clientFirstName?.trim() ?? "",
    lastName: draft.clientLastName?.trim() ?? "",
  };
}

export function isDraftIndividualCreatePath(draft: Draft | null): boolean {
  return Boolean(draft && !draft.clientId?.trim() && hasDraftClientData(draft));
}
